// 内容审核 - 领域类型与常量（词库 / 策略 / 日志）
// 说明：机审标签、词库分类按原型固定为常量，后续接入字典管理后改为字典取值。

// —— 词库 ——

// 词库识别结果（判定方向）：违规 / 疑似 / 放过
export type LexiconResult = 'VIOLATION' | 'SUSPECTED' | 'ALLOW'

// 匹配模式：精确 / 模糊（创建后不可变更）
export type MatchMode = 'EXACT' | 'FUZZY'

// 词库分类：政治 / 色情 / 暴恐 / 自定义库
export type LexiconCategory = 'POLITICS' | 'PORN' | 'TERROR' | 'CUSTOM'

// 关键词类型：普通词 / 组合词 / 英文
export type TermType = 'NORMAL' | 'COMPOSITE' | 'ENGLISH'

// 机审标签
export type MachineTag =
  | 'POLITICS'
  | 'PORN'
  | 'TERROR'
  | 'ILLEGAL'
  | 'ABUSE'
  | 'FINANCE'
  | 'AD'
  | 'SPAM'
  | 'VALUES'

export interface ReviewLexicon {
  id: number
  name: string
  result: LexiconResult
  matchMode: MatchMode
  category: LexiconCategory
  // 当前词库关键词数量（账号累计在组件内按词库求和）
  termCount: number
  updatedAt: string
}

export interface ReviewTerm {
  id: number
  lexiconId: number
  term: string
  machineTags: MachineTag[]
  type: TermType
  remark: string
  createdAt: string
}

export interface AddTermInput {
  lexiconId: number
  terms: string[]
  machineTags: MachineTag[]
  type: TermType
  remark: string
}

export interface SaveLexiconInput {
  name: string
  result: LexiconResult
  matchMode: MatchMode
  category: LexiconCategory
}

// —— 策略 ——

// 策略状态：生效中 / 已停用
export type PolicyStatus = 'ACTIVE' | 'DISABLED'

// 审核服务（首期仅文本内容安全）
export type ReviewService = 'TEXT'

export interface ReviewPolicy {
  id: string
  name: string
  scene: string
  service: ReviewService
  linkedLexiconIds: number[]
  status: PolicyStatus
  updatedAt: string
}

export interface SavePolicyInput {
  name: string
  scene: string
  service: ReviewService
  linkedLexiconIds: number[]
  status: PolicyStatus
}

// —— 日志 ——

// 日志识别结果（实际处理动作）：拦截 / 放行 / 人工待审
export type ReviewAction = 'BLOCK' | 'PASS' | 'HUMAN'

export interface ReviewLog {
  id: string
  module: string
  content: string
  requestId: string
  hitTerm: string
  hitPolicy: string
  action: ReviewAction
  machineTag: MachineTag
  service: ReviewService
  reviewedAt: string
  submittedAt: string
  tenant: string
  submitter: string
  // 命中位置（第 N 字符），详情页展示
  hitPosition: number
}

// —— 常量 ——

export const LEXICON_RESULT_OPTIONS: Array<{ value: LexiconResult; label: string }> = [
  { value: 'VIOLATION', label: '违规' },
  { value: 'SUSPECTED', label: '疑似' },
  { value: 'ALLOW', label: '放过' }
]

export const MATCH_MODE_OPTIONS: Array<{ value: MatchMode; label: string }> = [
  { value: 'EXACT', label: '精确匹配' },
  { value: 'FUZZY', label: '模糊匹配' }
]

export const LEXICON_CATEGORY_OPTIONS: Array<{ value: LexiconCategory; label: string }> = [
  { value: 'POLITICS', label: '政治' },
  { value: 'PORN', label: '色情' },
  { value: 'TERROR', label: '暴恐' },
  { value: 'CUSTOM', label: '自定义库' }
]

export const MACHINE_TAG_OPTIONS: Array<{ value: MachineTag; label: string }> = [
  { value: 'POLITICS', label: '政治' },
  { value: 'PORN', label: '色情' },
  { value: 'TERROR', label: '暴恐' },
  { value: 'ILLEGAL', label: '违法' },
  { value: 'ABUSE', label: '谩骂' },
  { value: 'FINANCE', label: '金融' },
  { value: 'AD', label: '广告' },
  { value: 'SPAM', label: '灌水' },
  { value: 'VALUES', label: '价值观' }
]

export const TERM_TYPE_OPTIONS: Array<{ value: TermType; label: string }> = [
  { value: 'NORMAL', label: '普通词' },
  { value: 'COMPOSITE', label: '组合词' },
  { value: 'ENGLISH', label: '英文' }
]

export const REVIEW_ACTION_OPTIONS: Array<{ value: ReviewAction; label: string }> = [
  { value: 'BLOCK', label: '拦截' },
  { value: 'PASS', label: '放行' },
  { value: 'HUMAN', label: '人工待审' }
]

export const POLICY_STATUS_OPTIONS: Array<{ value: PolicyStatus; label: string }> = [
  { value: 'ACTIVE', label: '生效中' },
  { value: 'DISABLED', label: '已停用' }
]

export const REVIEW_SERVICE_OPTIONS: Array<{ value: ReviewService; label: string }> = [
  { value: 'TEXT', label: '文本内容安全' }
]

// 单账号累计关键词上限 / 单次提交上限
export const TERM_ACCOUNT_LIMIT = 50000
export const TERM_BATCH_LIMIT = 2000

// —— 标签映射工具 ——

export function lexiconResultLabel(result: LexiconResult) {
  return LEXICON_RESULT_OPTIONS.find((option) => option.value === result)?.label ?? result
}

export function matchModeLabel(mode: MatchMode) {
  return MATCH_MODE_OPTIONS.find((option) => option.value === mode)?.label ?? mode
}

export function lexiconCategoryLabel(category: LexiconCategory) {
  return LEXICON_CATEGORY_OPTIONS.find((option) => option.value === category)?.label ?? category
}

export function machineTagLabel(tag: MachineTag) {
  return MACHINE_TAG_OPTIONS.find((option) => option.value === tag)?.label ?? tag
}

export function termTypeLabel(type: TermType) {
  return TERM_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type
}

export function reviewActionLabel(action: ReviewAction) {
  return REVIEW_ACTION_OPTIONS.find((option) => option.value === action)?.label ?? action
}

export function policyStatusLabel(status: PolicyStatus) {
  return POLICY_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status
}

export function reviewServiceLabel(service: ReviewService) {
  return REVIEW_SERVICE_OPTIONS.find((option) => option.value === service)?.label ?? service
}
